import * as React from "react"
import { Select as SelectPrimitive } from "@base-ui/react/select"

import { cn } from "@/lib/utils"
import { ChevronDownIcon, CheckIcon, ChevronUpIcon } from '@/lib/icons'

// Base UI's Select.Value displays the raw value unless the Root is given an
// `items` prop (or a render function). Without that, a Select whose values
// are keys like "all" / "bug" / "native" shows the identifier in the trigger
// instead of the human label defined in SelectItem children.
//
// Wrap Select.Root so it walks its children, collects every SelectItem's
// { value, label } pair, and passes them as `items`. Base UI then resolves
// the selected label automatically — all existing <SelectValue /> usages in
// the codebase start showing labels with no call-site changes.
function collectSelectItems(
  node: React.ReactNode,
): { value: unknown; label: React.ReactNode }[] {
  const out: { value: unknown; label: React.ReactNode }[] = []
  const visit = (n: React.ReactNode) => {
    React.Children.forEach(n, (c) => {
      if (!React.isValidElement(c)) return
      if (c.type === SelectItem) {
        const p = c.props as { value?: unknown; children?: React.ReactNode }
        if (p.value !== undefined) {
          out.push({ value: p.value, label: p.children })
        }
        return
      }
      const nested = (c.props as { children?: React.ReactNode } | null)?.children
      if (nested != null) visit(nested)
    })
  }
  visit(node)
  return out
}

function Select<Value, Multiple extends boolean | undefined = false>(
  props: SelectPrimitive.Root.Props<Value, Multiple>,
) {
  const { children, items, ...rest } = props

  // The derived `items` array MUST keep a stable reference across renders.
  // Base UI's Select.Root re-syncs its internal store in a layout effect every
  // time the `items` reference changes (SelectRoot useIsoLayoutEffect →
  // store.update → setState). Collecting items fresh each render (a new array
  // with new objects) therefore drives an infinite setState loop — React error
  // #185, "Maximum update depth exceeded". `useMemo([items, children])` does NOT
  // save us: `children` is a new reference on almost every render, so the memo
  // recomputes a new array each time.
  //
  // Cache the collected array and only swap in a new reference when the actual
  // value/label set changes (signature compare). Labels that aren't primitives
  // (React elements) don't participate in the signature — they're keyed off the
  // value, which is what Base UI resolves the trigger label against anyway.
  const cacheRef = React.useRef<{
    sig: string
    items: SelectPrimitive.Root.Props<Value, Multiple>["items"]
  } | null>(null)

  let derivedItems: SelectPrimitive.Root.Props<Value, Multiple>["items"]
  if (items !== undefined) {
    derivedItems = items
  } else {
    const collected = collectSelectItems(children)
    if (collected.length === 0) {
      derivedItems = undefined
    } else {
      const sig = JSON.stringify(
        collected.map((c) => [
          String(c.value),
          typeof c.label === "string" || typeof c.label === "number"
            ? String(c.label)
            : "",
        ]),
      )
      if (!cacheRef.current || cacheRef.current.sig !== sig) {
        cacheRef.current = {
          sig,
          items: collected as unknown as SelectPrimitive.Root.Props<Value, Multiple>["items"],
        }
      }
      derivedItems = cacheRef.current.items
    }
  }

  return (
    <SelectPrimitive.Root items={derivedItems} {...rest}>
      {children}
    </SelectPrimitive.Root>
  )
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn("flex flex-1 text-left", className)}
      {...props}
    />
  )
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: "sm" | "default"
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "flex w-fit items-center justify-between gap-1.5 rounded-lg border border-input bg-transparent py-2 pr-2 pl-2.5 text-sm whitespace-nowrap transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 data-placeholder:text-muted-foreground data-[size=default]:h-8 data-[size=sm]:h-7 data-[size=sm]:rounded-[min(var(--radius-md),10px)] *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={
          <ChevronDownIcon className="pointer-events-none size-4 text-muted-foreground" />
        }
      />
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  alignItemWithTrigger = true,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
  >) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn("relative isolate z-50 max-h-(--available-height) w-(--anchor-width) min-w-36 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 data-[align-trigger=true]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95", className )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectItem({
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-default items-center gap-1.5 rounded-md py-1 pr-8 pl-1.5 text-sm outline-hidden select-none focus:bg-accent focus:text-accent-foreground not-data-[variant=destructive]:focus:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
        }
      >
        <CheckIcon className="pointer-events-none" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "top-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <ChevronUpIcon
      />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <ChevronDownIcon
      />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectContent,
  SelectItem,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
}
